import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Toaster } from "sonner";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import ProfileSetup from "@/pages/ProfileSetup";
import Profile from "@/pages/Profile";
import Stories from "@/pages/Stories";
import CommunityChat from "@/pages/CommunityChat";
import CreateStory from "@/pages/CreateStory";
import StoryDetail from "@/pages/StoryDetail";
import Matches from "@/pages/Matches";
import Notifications from "@/pages/Notifications";
import Shop from "@/pages/Shop";
import BalloonGame from "@/pages/BalloonGame";
import EarnShare from "@/pages/EarnShare";
import Find from "@/pages/Find";
import FlexerBoard from "@/pages/FlexerBoard";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import Terms from "@/pages/Terms";
import ChildSafety from "@/pages/ChildSafety";
import AcceptLegal from "@/pages/AcceptLegal";
import AdminRoute from "@/components/AdminRoute";
import Admin from "@/pages/Admin";
import Groups from "@/pages/Groups";
import CreateGroup from "@/pages/CreateGroup";
import GroupDetail from "@/pages/GroupDetail";
import EditGroup from "@/pages/EditGroup";
import WhatsApp from "@/pages/WhatsApp";
import Support from "@/pages/Support";
import Invite from "@/pages/Invite";
import Navbar from "@/components/Navbar";
import GooglePlayBanner from "@/components/GooglePlayBanner";
import InstallPWA from "@/components/InstallPWA";

// =====================================================================
// Route guards
// =====================================================================

function RequireUser({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-sm text-[#6B6B70]">Loading…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/" replace />;
  return children;
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-sm text-[#6B6B70]">Loading…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/" replace />;
  if (user.legal_acceptance?.required !== false) {
    return <Navigate to="/accept-legal" replace />;
  }
  return children;
}

export function OnboardingCheck({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-sm text-[#6B6B70]">Loading…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/" replace />;
  if (user.legal_acceptance?.required !== false) {
    return <Navigate to="/accept-legal" replace />;
  }
  if (user.onboarding_complete === false) {
    return <Navigate to="/profile/setup" replace />;
  }
  return children;
}

// =====================================================================
// Layout
// =====================================================================

function AuthenticatedLayout({ children }) {
  return (
    <div className="min-h-screen bg-[#FAFAF7]">
      <Navbar />
      <main className="pt-16 md:pl-64">{children}</main>
      <GooglePlayBanner />
    </div>
  );
}

// =====================================================================
// Router
// =====================================================================

function AppRouter() {
  return (
    <Routes>
      {/* Public */}
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/child-safety" element={<ChildSafety />} />

      {/* Legacy redirects */}
      <Route path="/app" element={<Navigate to="/find" replace />} />
      <Route path="/discover" element={<Navigate to="/find" replace />} />
      <Route path="/friends" element={<Navigate to="/matches" replace />} />
      <Route path="/chat" element={<Navigate to="/matches" replace />} />

      {/* Legal consent — requires login, but NOT legal acceptance */}
      <Route
        path="/accept-legal"
        element={
          <RequireUser>
            <AcceptLegal />
          </RequireUser>
        }
      />
      <Route path="/accept-privacy" element={<Navigate to="/accept-legal" replace />} />

      {/* Onboarding — auth + legal, but NOT onboarding_complete */}
      <Route
        path="/profile/setup"
        element={
          <ProtectedRoute>
            <ProfileSetup />
          </ProtectedRoute>
        }
      />

      {/* Notifications — auth + legal, but NOT onboarding_complete */}
      <Route
        path="/notifications"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout>
              <Notifications />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />

      {/* Main app — requires login + legal + onboarding_complete */}
      <Route
        path="/find"
        element={
          <OnboardingCheck>
            <AuthenticatedLayout>
              <Find />
            </AuthenticatedLayout>
          </OnboardingCheck>
        }
      />
      <Route
        path="/matches"
        element={
          <OnboardingCheck>
            <AuthenticatedLayout>
              <Matches />
            </AuthenticatedLayout>
          </OnboardingCheck>
        }
      />
      <Route
        path="/whatsapp"
        element={
          <OnboardingCheck>
            <AuthenticatedLayout>
              <WhatsApp />
            </AuthenticatedLayout>
          </OnboardingCheck>
        }
      />
      <Route
        path="/stories"
        element={
          <OnboardingCheck>
            <AuthenticatedLayout>
              <Stories />
            </AuthenticatedLayout>
          </OnboardingCheck>
        }
      />
      <Route
        path="/stories/create"
        element={
          <OnboardingCheck>
            <AuthenticatedLayout>
              <CreateStory />
            </AuthenticatedLayout>
          </OnboardingCheck>
        }
      />
      <Route
        path="/stories/:storyId"
        element={
          <OnboardingCheck>
            <AuthenticatedLayout>
              <StoryDetail />
            </AuthenticatedLayout>
          </OnboardingCheck>
        }
      />
      <Route
        path="/chat/:chatType"
        element={
          <OnboardingCheck>
            <AuthenticatedLayout>
              <CommunityChat />
            </AuthenticatedLayout>
          </OnboardingCheck>
        }
      />
      <Route
        path="/shop"
        element={
          <OnboardingCheck>
            <AuthenticatedLayout>
              <Shop />
            </AuthenticatedLayout>
          </OnboardingCheck>
        }
      />
      <Route
        path="/earn-tokens"
        element={
          <OnboardingCheck>
            <AuthenticatedLayout>
              <BalloonGame />
            </AuthenticatedLayout>
          </OnboardingCheck>
        }
      />
      <Route
        path="/earn-tokens/share"
        element={
          <OnboardingCheck>
            <AuthenticatedLayout>
              <EarnShare />
            </AuthenticatedLayout>
          </OnboardingCheck>
        }
      />
      <Route
        path="/invite"
        element={
          <OnboardingCheck>
            <AuthenticatedLayout>
              <Invite />
            </AuthenticatedLayout>
          </OnboardingCheck>
        }
      />
      <Route
        path="/support"
        element={
          <OnboardingCheck>
            <AuthenticatedLayout>
              <Support />
            </AuthenticatedLayout>
          </OnboardingCheck>
        }
      />
      <Route
        path="/groups"
        element={
          <OnboardingCheck>
            <AuthenticatedLayout>
              <Groups />
            </AuthenticatedLayout>
          </OnboardingCheck>
        }
      />
      <Route
        path="/groups/create"
        element={
          <OnboardingCheck>
            <AuthenticatedLayout>
              <CreateGroup />
            </AuthenticatedLayout>
          </OnboardingCheck>
        }
      />
      <Route
        path="/groups/:groupId"
        element={
          <OnboardingCheck>
            <AuthenticatedLayout>
              <GroupDetail />
            </AuthenticatedLayout>
          </OnboardingCheck>
        }
      />
      <Route
        path="/groups/:groupId/edit"
        element={
          <OnboardingCheck>
            <AuthenticatedLayout>
              <EditGroup />
            </AuthenticatedLayout>
          </OnboardingCheck>
        }
      />
      <Route
        path="/profile"
        element={
          <OnboardingCheck>
            <AuthenticatedLayout>
              <Profile />
            </AuthenticatedLayout>
          </OnboardingCheck>
        }
      />
      <Route
        path="/flexer-board"
        element={
          <OnboardingCheck>
            <AuthenticatedLayout>
              <FlexerBoard />
            </AuthenticatedLayout>
          </OnboardingCheck>
        }
      />

      {/* Admin */}
      <Route
        path="/admin"
        element={
          <AdminRoute>
            <AuthenticatedLayout>
              <Admin />
            </AuthenticatedLayout>
          </AdminRoute>
        }
      />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <InstallPWA />
          <Toaster
            position="top-right"
            toastOptions={{
              style: {
                background: "#FFFFFF",
                border: "1px solid #E7E5E0",
                boxShadow:
                  "0 4px 12px -4px rgba(15, 15, 16, 0.08), 0 2px 6px -1px rgba(15, 15, 16, 0.04)",
                borderRadius: "0.5rem",
                color: "#0F0F10",
                fontSize: "0.875rem",
              },
            }}
          />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;